import { css, Global as EmotionGlobal } from "@emotion/react";
import { Theme, ThemeProvider } from "@modernatx/ui-kit-react";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import React from "react";
import { Descendant } from "slate";

import { BlockPage } from "@/components/BlockPage";
import { TextBlockProvider } from "@/components/TextBlock/TextBlockContext";
import { TextBlockRenderProvider } from "@/components/TextBlock/TextBlockRenderContext";
import { renderTextBlockStatic } from "@/components/TextBlock/TextBlockStatic/TextBlockStatic";
import { BlockPageProps } from "@/types/Block";
import { Experience, getExperience } from "@/utils/experience";
import { getIsProduction } from "@/utils/production";
import { fetchProfileText, getProfileTextIdsAndRemoveUndefinedValues } from "@/utils/profileText";

const TextBlockPopover = dynamic(
  () =>
    import("@/components/TextBlock/TextBlockEditor/TextBlockPopover").then(
      (res) => res.TextBlockPopover
    ),
  {
    ssr: false
  }
);

export const getServerSideProps: GetServerSideProps<{
  content: BlockPageProps | null;
  experience: Experience | null;
  isProduction: boolean;
  textBlocks: Record<string, Descendant[]> | null;
}> = async ({ req }) => {
  const experience = getExperience(req);
  const isProduction = getIsProduction();
  const profile = experience?.profile;
  let content: BlockPageProps | undefined;
  let textBlocks: Record<string, Descendant[]> | null;

  try {
    const contentModule = profile
      ? await import(`../../profiles${profile}`)
      : Promise.resolve(undefined);
    const ids: string[] = [];
    content = getProfileTextIdsAndRemoveUndefinedValues(contentModule.default, ids);
    textBlocks = await (profile
      ? fetchProfileText(experience.country, experience.language, ids).catch(() => null)
      : Promise.resolve(null));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Invalid profile path:", e);
    return {
      notFound: true
    };
  }

  if (
    !content ||
    (isProduction && !experience?.isProduction) ||
    (isProduction &&
      !!experience?.requiresReferer.length &&
      !experience?.requiresReferer.find((referer) => req.headers.referer?.includes(referer)))
  ) {
    return {
      props: getProfileTextIdsAndRemoveUndefinedValues(
        {
          content: null,
          experience,
          isProduction,
          textBlocks: null
        },
        []
      )
    };
  }

  return {
    props: getProfileTextIdsAndRemoveUndefinedValues(
      {
        content,
        experience,
        isProduction,
        textBlocks: textBlocks
      },
      []
    )
  };
};

const NextJsBlockPage = ({
  content,
  experience,
  textBlocks
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const searchParams = useSearchParams();
  const [renderTextBlock, setRenderTextBlock] = React.useState<{
    renderer: React.ComponentProps<typeof TextBlockRenderProvider>["renderTextBlock"];
  }>({ renderer: renderTextBlockStatic });
  const editing = !!searchParams.get("edit") && experience?.environment === "local";

  const handleTextBlockChange = React.useCallback(
    (id: string, contents: Descendant[]) => {
      const country = experience?.country;
      const language = experience?.language;

      if (language) {
        fetch(`/api/text`, {
          method: "POST",
          body: JSON.stringify({ country, id, contents, language })
        });
      }
    },
    [experience]
  );

  React.useEffect(() => {
    if (editing) {
      const loadFunction = async () => {
        const { renderTextBlockEditor } = await import(
          "@/components/TextBlock/TextBlockEditor/TextBlockEditor"
        );
        setRenderTextBlock({ renderer: renderTextBlockEditor });
      };

      loadFunction();
    } else if (renderTextBlock.renderer !== renderTextBlockStatic) {
      setRenderTextBlock({ renderer: renderTextBlockStatic });
    }
  }, [editing, renderTextBlock.renderer]);

  return (
    <TextBlockProvider
      textBlocks={textBlocks || {}}
      onTextBlockChange={editing ? handleTextBlockChange : () => {}}
    >
      <TextBlockRenderProvider editing={editing} renderTextBlock={renderTextBlock.renderer}>
        <EmotionGlobal
          styles={css`
            @font-face {
              font-family: "Knockout";
              font-display: block;
              font-style: normal;
              font-weight: 400;
              src: url(https://static.modernatx.com/fonts/Knockout-Medium.woff2) format("woff2");
            }
          `}
        />
        <BlockPage experience={experience} content={content} />
        {editing && (
          <ThemeProvider mode="dark">
            <TextBlockPopover theme={content?.theme as unknown as Theme} />
          </ThemeProvider>
        )}
      </TextBlockRenderProvider>
    </TextBlockProvider>
  );
};

export default NextJsBlockPage;
